// src/components/store/sections/product-cards/FlashUrgencyProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingBag, Clock, Flame, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

export default function FlashUrgencyProductCard({
  product,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    comparePrice,
    discountPercent,
    added,
    timer,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  return (
    <div className="w-full h-full bg-white rounded-3xl border border-slate-200/80 p-4 shadow-sm hover:shadow-lg transition-all flex flex-col justify-between group select-none">
      <div>
        <Link
          href={productUrl}
          className="relative w-full h-44 rounded-2xl bg-slate-50 overflow-hidden flex items-center justify-center p-2 mb-3 block"
        >
          <span className="absolute top-2.5 left-2.5 px-2.5 py-0.5 rounded-full bg-rose-600 text-white text-[10px] font-black z-10 flex items-center gap-1">
            <Flame className="w-3 h-3 fill-white" /> {discountPercent}% OFF
          </span>
          <Image
            src={productImg}
            alt={productName}
            fill
            unoptimized
            className="object-contain p-2 group-hover:scale-105 transition-transform duration-300"
          />
        </Link>

        <div className="flex items-center justify-between text-[10px] text-slate-400 font-bold mb-1">
          <span className="truncate max-w-[120px]">{categoryName}</span>
          <span className="flex items-center gap-0.5 text-amber-500">
            <Star className="w-3 h-3 fill-amber-400" /> {rating.toFixed(1)}
          </span>
        </div>

        <Link
          href={productUrl}
          className="text-sm font-black text-slate-800 hover:text-blue-600 line-clamp-1 block"
        >
          {productName}
        </Link>

        <div className="flex items-baseline gap-2 mt-2 font-mono">
          <span className="text-lg font-black text-slate-900">৳{currentPrice.toLocaleString()}</span>
          {comparePrice > currentPrice && (
            <span className="text-xs text-slate-400 line-through">৳{comparePrice.toLocaleString()}</span>
          )}
        </div>
      </div>

      <div className="space-y-2 mt-3 pt-2.5 border-t border-slate-100">
        <div className="text-[10px] font-mono text-slate-600 flex items-center justify-between font-bold bg-rose-50/60 px-2.5 py-1.5 rounded-lg border border-rose-100">
          <span className="text-rose-600 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Flash Ends:
          </span>
          <span>
            {String(timer.hours).padStart(2, "0")}h:{String(timer.minutes).padStart(2, "0")}m:
            <span className="text-rose-600">{String(timer.seconds).padStart(2, "0")}s</span>
          </span>
        </div>

        <button
          type="button"
          onClick={(e) => handleAddToCart(e)}
          className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 cursor-pointer active:scale-95 transition-all"
        >
          {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingBag className="w-3.5 h-3.5" />}
          <span>{added ? "Added" : "Add to Cart"}</span>
        </button>
      </div>
    </div>
  );
}
